import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { Button } from "../../../components/ui";
import { useShipmentStatuses, useShipmentContacts, type ShipmentContact } from "../../../features/shipments/queries";
import api from "../../../utils/api";
import LocationPicker from "./LocationPicker";
import CarrierTrackingFields, {
  EMPTY_CARRIER_DRAFT,
  isValidCarrierNumber,
  type CarrierTrackingDraft,
} from "./CarrierTrackingFields";
import MerchandiseEditor from "./MerchandiseEditor";
import {
  draftsToMerchandise,
  validateMerchandiseDrafts,
  type MerchandiseDraft,
  type MerchandiseDraftErrors,
} from "../../../features/shipments/merchandiseDraft";
import { trackShipmentFormValidationFailed } from "../../../analytics/shipmentsAnalytics";
import { getTripCoverage, hasLiveAssignment } from "../../../features/shipments/tripCoverage";
import { INCOTERMS } from "../../../utils/documentTypes";

interface Location {
  lat: number;
  long: number;
  address: string;
}

interface NotificationRecipient {
  id: string;
  name: string;
  email: string;
  phone: string;
  channels: { email: boolean; sms: boolean };
}

type NotificationMode = "on_change" | "interval";

interface ShipmentNotification {
  enabled: boolean;
  recipients: NotificationRecipient[];
  mode: NotificationMode;
  intervalHours: number;
}

/** Valores iniciales opcionales (p. ej. desde una operación del Radar de Correo). */
export interface NewShipmentInitial {
  shipmentName?: string;
  code?: string;
  carrierType?: "ground" | "air" | "maritime";
  /** Expediente documental (CC-CONSOLE #1632): dirección e Incoterm del envío. */
  direction?: "importacion" | "exportacion" | "nacional";
  incoterm?: string;
  note?: string;
}

interface NewShipmentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  initial?: NewShipmentInitial;
}

function newRecipient(): NotificationRecipient {
  return {
    id: `nr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: "",
    email: "",
    phone: "",
    channels: { email: true, sms: true },
  };
}

export default function NewShipmentModal({ open, onClose, onSubmit, initial }: NewShipmentModalProps) {
  const { t } = useTranslation("envios");
  const { data: statusesData } = useShipmentStatuses();

  const [clientInfo, setClientInfo] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
  });
  const [shipmentName, setShipmentName] = useState("");
  const [origin, setOrigin] = useState<Location | null>(null);
  const [destination, setDestination] = useState<Location | null>(null);
  const [code, setCode] = useState("");
  const [carrierType, setCarrierType] = useState("ground");
  // Expediente documental (CC-CONSOLE #1632): dirección e Incoterm deciden qué
  // documentos lleva el envío. Opcionales: sin dirección cuenta como nacional.
  const [direction, setDirection] = useState<"" | "importacion" | "exportacion" | "nacional">("");
  const [incoterm, setIncoterm] = useState("");
  const [carrierDraft, setCarrierDraft] = useState<CarrierTrackingDraft>(EMPTY_CARRIER_DRAFT);
  const [carrierError, setCarrierError] = useState<string | null>(null);
  const [statusId, setStatusId] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [expandedMap, setExpandedMap] = useState<"origin" | "destination" | null>(null);
  const [trackingMode, setTrackingMode] = useState<"manual" | "by_unit" | "by_trip">("manual");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [selectedTripId, setSelectedTripId] = useState("");
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [trips, setTrips] = useState<any[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [onlyAssignedTrips, setOnlyAssignedTrips] = useState(true);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [recipients, setRecipients] = useState<NotificationRecipient[]>(() => [newRecipient()]);
  const [notifMode, setNotifMode] = useState<NotificationMode>("on_change");
  const [notifIntervalHours, setNotifIntervalHours] = useState(6);
  const [selectedContacts, setSelectedContacts] = useState<ShipmentContact[]>([]);
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [newContactForm, setNewContactForm] = useState({ contact_name: "", contact_email: "", contact_phone: "" });
  const [creatingContact, setCreatingContact] = useState(false);
  const [merchandiseDrafts, setMerchandiseDrafts] = useState<MerchandiseDraft[]>([]);
  const [merchandiseErrors, setMerchandiseErrors] = useState<
    Record<string, MerchandiseDraftErrors>
  >({});
  const [contactError, setContactError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const statusInitializedRef = useRef(false);
  const { data: contactsData, refetch: refetchContacts } = useShipmentContacts();

  const trackingModes = [
    { key: "manual" as const, label: "Manual", desc: "Tú actualizas el estado a mano", icon: "ri-user-line" },
    { key: "by_unit" as const, label: "Por unidad", desc: "Sigue el movimiento y GPS de la unidad", icon: "ri-truck-line" },
    { key: "by_trip" as const, label: "Por viaje", desc: "Se sincroniza con el viaje", icon: "ri-route-line" },
  ];

  const updateRecipient = (id: string, patch: Partial<NotificationRecipient>) => {
    setRecipients((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const addRecipient = () => {
    setRecipients((prev) => [...prev, newRecipient()]);
  };

  const removeRecipient = (id: string) => {
    setRecipients((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  };

  const addContact = (contact: ShipmentContact) => {
    if (!selectedContacts.find((c) => c.id === contact.id)) {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const removeContact = (contactId: string) => {
    setSelectedContacts((prev) => prev.filter((c) => c.id !== contactId));
  };

  const handleCreateContact = async () => {
    setContactError("");

    if (!companyId) {
      setContactError("No se encontró la compañía. Por favor, intenta de nuevo.");
      return;
    }

    if (!newContactForm.contact_name.trim()) {
      setContactError("El nombre es obligatorio");
      return;
    }
    if (!newContactForm.contact_email.trim()) {
      setContactError("El email es obligatorio");
      return;
    }
    if (!newContactForm.contact_phone.trim()) {
      setContactError("El teléfono es obligatorio");
      return;
    }

    setCreatingContact(true);
    try {
      const response = await api.post("/contact", {
        contact_name: newContactForm.contact_name.trim(),
        contact_email: newContactForm.contact_email.trim(),
        contact_phone: newContactForm.contact_phone.trim(),
        company_id: companyId,
      });

      const createdContact: ShipmentContact = response.data.data;
      if (createdContact) {
        setSelectedContacts([...selectedContacts, createdContact]);
      }

      setNewContactForm({ contact_name: "", contact_email: "", contact_phone: "" });
      setShowCreateContact(false);
      setContactError("");

      // Refrescar la lista de contactos para mantener sincronizado el select
      await refetchContacts();
    } catch (err: any) {
      let errorMsg = "Error al crear el contacto";

      // Extraer mensaje del backend
      const messageObj = err?.response?.data?.meta?.message;
      if (messageObj && typeof messageObj === "object") {
        // El error viene en formato { "400": [...], "422": [...] }
        for (const key in messageObj) {
          const errorArray = messageObj[key];
          if (Array.isArray(errorArray) && errorArray.length > 0) {
            errorMsg = errorArray[0]?.msg || errorArray[0]?.message || errorMsg;
            break;
          }
        }
      } else if (typeof messageObj === "string") {
        errorMsg = messageObj;
      } else if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.message) {
        errorMsg = err.message;
      }

      setContactError(errorMsg);
      console.error("Error creating contact:", err);
    } finally {
      setCreatingContact(false);
    }
  };

  const generateTrackingNumber = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const generateSegment = () => {
      let segment = "";
      for (let i = 0; i < 3; i++) {
        segment += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return segment;
    };
    return `WK-${generateSegment()}-${generateSegment()}-MX`;
  };

  const allowedStatusIds = [
    "mVnsoQwNQwbgQ85MQkJ5",
    "i2ZOgzPRWdBIQlR7l0Fw",
    "4l6RmJmJoXaFLlyzuDk2",
    "GN7ettzJj3tO83MTmk21",
  ];

  const filteredStatuses = statusesData?.data?.filter((status) =>
    allowedStatusIds.includes(status.id),
  ) || [];

  useEffect(() => {
    if (open) {
      // Generate tracking number (o el que traiga `initial`, p. ej. la guía detectada en el correo)
      setCode(initial?.code || generateTrackingNumber());
      if (initial?.shipmentName) setShipmentName(initial.shipmentName);
      if (initial?.carrierType) setCarrierType(initial.carrierType);
      if (initial?.direction) setDirection(initial.direction);
      if (initial?.incoterm) setIncoterm(initial.incoterm.toUpperCase());
      if (initial?.note) setNote(initial.note);

      // Sincronizar companyId desde localStorage
      const savedCompanyId = typeof window !== "undefined" ? localStorage.getItem("companyId") : null;
      console.log("Modal opened, companyId from localStorage:", savedCompanyId);
      setCompanyId(savedCompanyId);

      // Set default status to the first one available (only once)
      if (filteredStatuses.length > 0 && !statusInitializedRef.current) {
        setStatusId(filteredStatuses[0].id);
        statusInitializedRef.current = true;
      }
    } else {
      // Reset the ref when modal closes
      statusInitializedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    const loadVehicles = async () => {
      setLoadingVehicles(true);
      try {
        const response = await api.get("/deviceList", { params: { limit: 500, page: 1 } });
        setVehicles(response.data?.data || []);
      } catch (err) {
        console.error("Error loading devices:", err);
        setVehicles([]);
      } finally {
        setLoadingVehicles(false);
      }
    };

    if (trackingMode === "by_unit" && open) {
      loadVehicles();
    }
  }, [trackingMode, open]);

  useEffect(() => {
    const loadTrips = async () => {
      setLoadingTrips(true);
      try {
        const response = await api.get("/tripsList", { params: { limit: 500, page: 1 } });
        setTrips(response.data?.data || []);
      } catch (err) {
        console.error("Error loading trips:", err);
        setTrips([]);
      } finally {
        setLoadingTrips(false);
      }
    };

    if (trackingMode === "by_trip" && open) {
      loadTrips();
    }
  }, [trackingMode, open]);


  const visibleTrips = onlyAssignedTrips ? trips.filter(hasLiveAssignment) : trips;
  const selectedTrip = trips.find((trip: any) => trip.id === selectedTripId);
  const tripCoverage = selectedTrip ? getTripCoverage(selectedTrip) : null;

  const switchToUnitMode = () => {
    setSelectedVehicleId(tripCoverage?.lastClosedDeviceId ?? "");
    setSelectedTripId("");
    setTrackingMode("by_unit");
  };

  const handleOnlyAssignedChange = (checked: boolean) => {
    setOnlyAssignedTrips(checked);
    checked && selectedTrip && !hasLiveAssignment(selectedTrip) && setSelectedTripId("");
  };

  const carrierOptions = [
    { value: "ground", label: "Terrestre" },
    { value: "air", label: "Aéreo" },
    { value: "maritime", label: "Marítimo" },
    { value: "parcel", label: t("transport.parcel") },
  ];

  if (!open) return null;

  console.log("=== Button State ===");
  console.log("companyId:", companyId);
  console.log("isSubmitting:", isSubmitting);
  console.log("Should be disabled:", isSubmitting || !companyId);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Cada rechazo reporta el NOMBRE del campo, nunca lo que el usuario
    // escribio. Es la senal mas directa de donde se abandona el alta de un
    // envio, que es el formulario mas largo de la app.
    const rejectField = (field: string, message: string) => {
      trackShipmentFormValidationFailed(field);
      setError(message);
    };

    if (!clientInfo.client_name.trim()) {
      rejectField("client_name", "El nombre del cliente es obligatorio");
      return;
    }

    if (!clientInfo.client_email.trim()) {
      rejectField("client_email", "El email del cliente es obligatorio");
      return;
    }

    if (!clientInfo.client_phone.trim()) {
      rejectField("client_phone", "El teléfono del cliente es obligatorio");
      return;
    }

    if (!shipmentName.trim()) {
      rejectField("shipment_name", "El nombre del envío es obligatorio");
      return;
    }

    if (!origin) {
      rejectField("origin", "Debes seleccionar el origen");
      return;
    }

    if (!destination) {
      rejectField("destination", "Debes seleccionar el destino");
      return;
    }

    if (!code.trim()) {
      rejectField("tracking_code", "El código de rastreo es obligatorio");
      return;
    }

    const carrierNumber = carrierDraft.number.replace(/\s+/g, "");
    if (carrierNumber && !isValidCarrierNumber(carrierNumber)) {
      setCarrierError(t("carrier.invalidNumber"));
      rejectField("carrier_tracking_number", t("carrier.invalidNumber"));
      return;
    }
    setCarrierError(null);

    const foundMerchandiseErrors = validateMerchandiseDrafts(merchandiseDrafts);
    setMerchandiseErrors(foundMerchandiseErrors);
    if (Object.keys(foundMerchandiseErrors).length > 0) {
      rejectField("merchandise", t("merchandise.fixErrors"));
      return;
    }

    // Crear event_history inicial con pickup
    const initialEvent = {
      device_id: trackingMode === "by_unit" ? selectedVehicleId : "",
      lat: origin.lat,
      long: origin.long,
      address: origin.address,
      event_type: "pickup",
      date: new Date().toISOString(),
      note: "",
      file_url: "",
    };

    const merchandiseData = draftsToMerchandise(merchandiseDrafts);

    const shipmentPayload = {
      company_id: companyId,
      client_info: clientInfo,
      shipment_name: shipmentName.trim(),
      origin,
      destination,
      tracking_number: code.trim(),
      transportation_type: carrierType,
      ...(direction && { direction }),
      ...(incoterm && { incoterm }),
      status_id: statusId,
      smart_tracking: trackingMode,
      ...(trackingMode === "by_unit" && { actual_device_id: selectedVehicleId }),
      ...(trackingMode === "by_trip" && { trip_id: selectedTripId }),
      note,
      event_history: [initialEvent],
      ...(selectedContacts.length > 0 && { contacts: selectedContacts.map((c) => ({ id: c.id })) }),
      ...(notifEnabled && { notification_frequency: notifMode === "interval" ? `${notifIntervalHours}h` : "change" }),
      merchandise: merchandiseData,
      ...(carrierNumber && {
        carrier_tracking_number: carrierNumber,
        ...(carrierDraft.carrierCode && { carrier_code: carrierDraft.carrierCode }),
      }),
    };

    console.log("=== DEBUG Shipment Creation ===");
    console.log("trackingMode:", trackingMode);
    console.log("selectedVehicleId:", selectedVehicleId);
    console.log("selectedTripId:", selectedTripId);
    console.log("Shipment Payload:", shipmentPayload);

    setIsSubmitting(true);
    setError("");

    try {
      await onSubmit(shipmentPayload);

      // Reset form only if submission was successful
      setClientInfo({
        client_name: "",
        client_email: "",
        client_phone: "",
      });
      setShipmentName("");
      setOrigin(null);
      setDestination(null);
      setCode("");
      setCarrierType("ground");
      setCarrierDraft(EMPTY_CARRIER_DRAFT);
      setCarrierError(null);
      setStatusId(filteredStatuses?.[0]?.id || "");
      setError("");
      setExpandedMap(null);
      setTrackingMode("manual");
      setSelectedVehicleId("");
      setSelectedTripId("");
      setNotifEnabled(false);
      setRecipients([newRecipient()]);
      setNotifMode("on_change");
      setNotifIntervalHours(6);
      setSelectedContacts([]);
      setShowCreateContact(false);
      setNewContactForm({ contact_name: "", contact_email: "", contact_phone: "" });
      setContactError("");
      setMerchandiseDrafts([]);
      setMerchandiseErrors({});
    } catch (err: any) {
      setError(err.message || "Error al crear el envío");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 py-6">
      <div className="bg-card border border-line rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-auto p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-content">Nuevo envío</h3>
            <p className="text-sm text-content-muted mt-1">Registra un nuevo envío con ubicación en mapa</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-content-muted hover:text-content"
          >
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 mb-6">
            {/* Información del Cliente */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-content-muted mb-1.5">
                  Nombre del cliente *
                </label>
                <input
                  type="text"
                  value={clientInfo.client_name}
                  onChange={(e) => setClientInfo({ ...clientInfo, client_name: e.target.value })}
                  placeholder="Ej. Juan Pérez"
                  className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm placeholder-content-muted focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-content-muted mb-1.5">
                  Email del cliente *
                </label>
                <input
                  type="email"
                  value={clientInfo.client_email}
                  onChange={(e) => setClientInfo({ ...clientInfo, client_email: e.target.value })}
                  placeholder="Ej. juan@example.com"
                  className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm placeholder-content-muted focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-content-muted mb-1.5">
                  Teléfono del cliente *
                </label>
                <input
                  type="tel"
                  value={clientInfo.client_phone}
                  onChange={(e) => setClientInfo({ ...clientInfo, client_phone: e.target.value })}
                  placeholder="Ej. +52 123 4567890"
                  className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm placeholder-content-muted focus:outline-none focus:border-primary-500"
                  required
                />
              </div>
            </div>

            {/* Nombre del Envío */}
            <div>
              <label className="block text-xs text-content-muted mb-1.5">
                Nombre del envío *
              </label>
              <input
                type="text"
                value={shipmentName}
                onChange={(e) => setShipmentName(e.target.value)}
                placeholder="Ej. Shipment CDMX a Monterrey"
                className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm placeholder-content-muted focus:outline-none focus:border-primary-500"
                required
              />
            </div>

            {/* Código y Tipo de transporte */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-content-muted mb-1.5">
                  Código de rastreo *
                </label>
                <input
                  type="text"
                  value={code}
                  placeholder="WK-XXX-YYY-MX"
                  className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm font-mono placeholder-content-muted focus:outline-none focus:border-primary-500"
                  readOnly
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-content-muted mb-1.5">
                  Tipo de transporte *
                </label>
                <select
                  value={carrierType}
                  onChange={(e) => setCarrierType(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                >
                  {carrierOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Dirección e Incoterm (expediente documental, CC-CONSOLE #1632) — opcionales */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="shipment-direction" className="block text-xs text-content-muted mb-1.5">
                  {t("form.direction")}
                </label>
                <select
                  id="shipment-direction"
                  value={direction}
                  onChange={(e) => setDirection(e.target.value as typeof direction)}
                  className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                >
                  <option value="">{t("form.directionUnset")}</option>
                  <option value="nacional">{t("form.directionDomestic")}</option>
                  <option value="importacion">{t("form.directionImport")}</option>
                  <option value="exportacion">{t("form.directionExport")}</option>
                </select>
              </div>
              <div>
                <label htmlFor="shipment-incoterm" className="block text-xs text-content-muted mb-1.5">
                  {t("form.incoterm")}
                </label>
                <select
                  id="shipment-incoterm"
                  value={incoterm}
                  onChange={(e) => setIncoterm(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                >
                  <option value="">{t("form.incotermUnset")}</option>
                  {INCOTERMS.map((ic) => (
                    <option key={ic} value={ic}>
                      {ic}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-content-muted">{t("form.incotermHint")}</p>
              </div>
            </div>

            {/* Código de rastreo de paquetería (17TRACK) — opcional */}
            <div className="rounded-xl border border-line p-4">
              <div className="mb-3">
                <p className="text-sm font-semibold text-content">{t("carrier.formTitle")}</p>
                <p className="mt-0.5 text-xs text-content-muted">{t("carrier.formHint")}</p>
              </div>
              <CarrierTrackingFields
                value={carrierDraft}
                onChange={(next) => {
                  setCarrierDraft(next);
                  if (carrierError) setCarrierError(null);
                  if (next.number.trim() && carrierType === "ground" && !carrierDraft.number.trim()) {
                    setCarrierType("parcel");
                  }
                }}
                error={carrierError}
              />
            </div>

            {/* Estado del envío */}
            <div>
              <label className="block text-xs text-content-muted mb-1.5">
                Estado del envío *
              </label>
              <select
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                required
              >
                <option value="">-- Selecciona un estado --</option>
                {filteredStatuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {t(`statusNames.${status.id}`) || status.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Seguimiento inteligente */}
            <div>
              <label className="block text-xs text-content-muted mb-1.5">
                Seguimiento inteligente
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {trackingModes.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => {
                      console.log("Tracking mode changed to:", m.key);
                      setTrackingMode(m.key);
                    }}
                    className={`flex flex-col items-start gap-1.5 p-3 rounded-xl border text-left transition-all cursor-pointer ${
                      trackingMode === m.key
                        ? "border-primary-500 bg-primary-50 ring-1 ring-primary-200"
                        : "border-line bg-surface hover:border-primary-300"
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                      trackingMode === m.key
                        ? "bg-primary-500 text-white"
                        : "bg-subtle text-content-secondary"
                    }`}>
                      <i className={`${m.icon} text-sm`} />
                    </span>
                    <span className="text-[12.5px] font-semibold text-content">{m.label}</span>
                    <span className="text-[10.5px] text-content-muted leading-snug">{m.desc}</span>
                  </button>
                ))}
              </div>

              {trackingMode === "by_unit" && (
                <div className="mt-3">
                  <select
                    value={selectedVehicleId}
                    onChange={(e) => setSelectedVehicleId(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                    disabled={loadingVehicles}
                  >
                    <option value="">
                      {loadingVehicles ? "Cargando dispositivos..." : "-- Selecciona un dispositivo --"}
                    </option>
                    {vehicles.map((device: any) => (
                      <option key={device.id} value={device.id}>
                        {device.name || device.model || device.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {trackingMode === "by_trip" && (
                <div className="mt-3 space-y-2">
                  <label className="flex items-center gap-2 text-xs text-content-secondary">
                    <input
                      type="checkbox"
                      checked={onlyAssignedTrips}
                      onChange={(e) => handleOnlyAssignedChange(e.target.checked)}
                    />
                    {t("tripPicker.onlyAssigned")}
                  </label>
                  <select
                    value={selectedTripId}
                    onChange={(e) => setSelectedTripId(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                    disabled={loadingTrips}
                  >
                    <option value="">
                      {loadingTrips ? "Cargando viajes..." : "-- Selecciona un viaje --"}
                    </option>
                    {visibleTrips.map((trip: any) => (
                      <option key={trip.id} value={trip.id}>
                        {trip.name || trip.title || trip.id}
                        {hasLiveAssignment(trip) ? "" : ` (${t("tripPicker.optionNoUnit")})`}
                      </option>
                    ))}
                  </select>
                  {!loadingTrips && onlyAssignedTrips && visibleTrips.length === 0 && (
                    <p className="text-xs text-content-muted">{t("tripPicker.noneAssigned")}</p>
                  )}
                  {tripCoverage?.state === "live" && (
                    <p className="text-xs text-content-secondary">
                      {t("tripPicker.liveTitle")}{" "}
                      {tripCoverage.liveUnits.map((unit) => unit.label).join(", ")}
                    </p>
                  )}
                  {tripCoverage && tripCoverage.state !== "live" && (
                    <div className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
                      <i className="ri-route-line mt-0.5" />
                      <div className="space-y-1.5">
                        <p>
                          {tripCoverage.state === "closed_only"
                            ? t("tripPicker.closedWarning")
                            : t("tripPicker.noneWarning")}{" "}
                          {t("tripPicker.canStillCreate")}
                        </p>
                        <button
                          type="button"
                          onClick={switchToUnitMode}
                          className="font-semibold underline"
                        >
                          {t("tripPicker.switchToUnit")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Ubicaciones */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-content">
                    Origen *
                  </label>
                  {origin && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      ✓ Seleccionado
                    </span>
                  )}
                </div>

                {expandedMap === "origin" ? (
                  <LocationPicker
                    onLocationSelect={(loc) => {
                      setOrigin(loc);
                      setExpandedMap(null);
                    }}
                    initialLocation={origin || undefined}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setExpandedMap("origin")}
                    className="w-full p-3 text-left border border-line rounded-lg bg-surface hover:bg-subtle transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-content-muted">
                          {origin ? origin.address : "Selecciona ubicación en el mapa"}
                        </p>
                        {origin && (
                          <p className="text-xs text-content-muted font-mono">
                            {origin.lat.toFixed(4)}, {origin.long.toFixed(4)}
                          </p>
                        )}
                      </div>
                      <i className="ri-map-pin-line text-primary-500 ml-2 flex-shrink-0" />
                    </div>
                  </button>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-content">
                    Destino *
                  </label>
                  {destination && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      ✓ Seleccionado
                    </span>
                  )}
                </div>

                {expandedMap === "destination" ? (
                  <LocationPicker
                    onLocationSelect={(loc) => {
                      setDestination(loc);
                      setExpandedMap(null);
                    }}
                    initialLocation={destination || undefined}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setExpandedMap("destination")}
                    className="w-full p-3 text-left border border-line rounded-lg bg-surface hover:bg-subtle transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-content-muted">
                          {destination ? destination.address : "Selecciona ubicación en el mapa"}
                        </p>
                        {destination && (
                          <p className="text-xs text-content-muted font-mono">
                            {destination.lat.toFixed(4)}, {destination.long.toFixed(4)}
                          </p>
                        )}
                      </div>
                      <i className="ri-map-pin-line text-primary-500 ml-2 flex-shrink-0" />
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Mercancía */}
            <div className="rounded-xl border border-line p-4">
              <label className="block text-sm font-semibold text-content mb-3">
                {t("merchandise.optionalTitle")}
              </label>
              <MerchandiseEditor
                drafts={merchandiseDrafts}
                onChange={setMerchandiseDrafts}
                errors={merchandiseErrors}
                disabled={isSubmitting}
              />
            </div>

            {/* Notificaciones al cliente */}
            <div className="rounded-xl border border-line p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-sm font-semibold text-content">Notificaciones al cliente</span>
                  <p className="text-xs text-content-muted mt-0.5">
                    Avisa por email o SMS a uno o varios destinatarios con el estado de la carga
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotifEnabled(!notifEnabled)}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    notifEnabled
                      ? "bg-primary-500 text-white"
                      : "bg-subtle text-content-secondary"
                  }`}
                >
                  {notifEnabled ? "Activado" : "Desactivado"}
                </button>
              </div>

              {notifEnabled && (
                <div className="mt-4 space-y-4">

                  {/* Contactos/Destinatarios del Envío */}
                  <div>
                    <label className="block text-sm font-semibold text-content mb-2">
                      Contactos del Envío
                    </label>
                    <div className="space-y-3">
                      {/* Select de contactos */}
                      <div>
                        <select
                          value=""
                          onChange={(e) => {
                            const contact = contactsData?.data.find((c) => c.id === e.target.value);
                            if (contact) addContact(contact);
                          }}
                          className="w-full px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                        >
                          <option value="">-- Selecciona un contacto --</option>
                          {contactsData?.data
                            ?.filter((c) => !selectedContacts.find((sc) => sc.id === c.id))
                            .map((contact) => (
                              <option key={contact.id} value={contact.id}>
                                {contact.contact_name} ({contact.contact_email})
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Botón crear contacto */}
                      <button
                        type="button"
                        onClick={() => setShowCreateContact(!showCreateContact)}
                        className="w-full px-3 py-2 rounded-lg border border-dashed border-primary-300 bg-primary-50/50 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <i className="ri-add-line" />
                        Crear nuevo contacto
                      </button>

                      {/* Formulario crear contacto */}
                      {showCreateContact && (
                        <div className="rounded-lg border border-primary-200 bg-primary-50/30 p-3 space-y-2">
                          {contactError && (
                            <div className="p-2.5 rounded-lg bg-red-100/80 text-red-700 text-xs font-medium">
                              {contactError}
                            </div>
                          )}
                          <input
                            type="text"
                            value={newContactForm.contact_name}
                            onChange={(e) => setNewContactForm({ ...newContactForm, contact_name: e.target.value })}
                            placeholder="Nombre"
                            className="w-full px-3 py-2 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                          />
                          <input
                            type="email"
                            value={newContactForm.contact_email}
                            onChange={(e) => setNewContactForm({ ...newContactForm, contact_email: e.target.value })}
                            placeholder="Email"
                            className="w-full px-3 py-2 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                          />
                          <input
                            type="tel"
                            value={newContactForm.contact_phone}
                            onChange={(e) => setNewContactForm({ ...newContactForm, contact_phone: e.target.value })}
                            placeholder="Teléfono"
                            className="w-full px-3 py-2 rounded-lg border border-line bg-surface text-content text-sm focus:outline-none focus:border-primary-500"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setShowCreateContact(false);
                                setContactError("");
                              }}
                              className="flex-1 px-3 py-2 rounded-lg border border-line text-content-muted text-sm hover:bg-subtle transition-colors"
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              onClick={handleCreateContact}
                              disabled={creatingContact}
                              className="flex-1 px-3 py-2 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50"
                            >
                              {creatingContact ? "Creando..." : "Agregar"}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Lista de contactos seleccionados */}
                      {selectedContacts.length > 0 && (
                        <div className="max-h-[200px] overflow-y-auto space-y-2 pr-1">
                          {selectedContacts.map((contact) => (
                            <div
                              key={contact.id}
                              className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-subtle border border-line"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-content">{contact.contact_name}</p>
                                <p className="text-xs text-content-muted">{contact.contact_email}</p>
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => removeContact(contact.id)}
                                  className="p-1.5 rounded text-content-muted hover:text-orange-500 hover:bg-orange-50 transition-colors"
                                  title="Quitar de la lista"
                                >
                                  <i className="ri-close-line text-lg" />
                                </button>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const result = await Swal.fire({
                                      title: "Eliminar contacto",
                                      text: `¿Estás seguro de que deseas eliminar "${contact.contact_name}" de la base de datos? Esta acción no se puede deshacer.`,
                                      icon: "warning",
                                      showCancelButton: true,
                                      confirmButtonColor: "#ef4444",
                                      cancelButtonColor: "#6b7280",
                                      confirmButtonText: "Eliminar",
                                      cancelButtonText: "Cancelar",
                                    });

                                    if (result.isConfirmed) {
                                      try {
                                        await api.delete("/contact", { params: { id: contact.id } });
                                        removeContact(contact.id);
                                        await refetchContacts();
                                        Swal.fire({
                                          title: "Eliminado",
                                          text: "El contacto ha sido eliminado exitosamente.",
                                          icon: "success",
                                          timer: 2000,
                                        });
                                      } catch (err) {
                                        console.error("Error deleting contact:", err);
                                        Swal.fire({
                                          title: "Error",
                                          text: "No se pudo eliminar el contacto. Intenta de nuevo.",
                                          icon: "error",
                                        });
                                      }
                                    }
                                  }}
                                  className="p-1.5 rounded text-content-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                                  title="Eliminar de la base de datos"
                                >
                                  <i className="ri-delete-bin-line text-lg" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <span className="text-sm font-semibold text-content-secondary block mb-1.5">
                      Frecuencia de aviso
                    </span>
                    <div className="flex bg-subtle border border-line rounded-full p-1 gap-1 w-fit">
                      <button
                        type="button"
                        onClick={() => setNotifMode("on_change")}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                          notifMode === "on_change"
                            ? "bg-primary-500 text-white"
                            : "text-content-muted hover:text-content"
                        }`}
                      >
                        Al cambiar
                      </button>
                      <button
                        type="button"
                        onClick={() => setNotifMode("interval")}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                          notifMode === "interval"
                            ? "bg-primary-500 text-white"
                            : "text-content-muted hover:text-content"
                        }`}
                      >
                        Periódico
                      </button>
                    </div>
                  </div>

                  {notifMode === "interval" && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-content-secondary whitespace-nowrap">
                        Enviar cada
                      </span>
                      <select
                        value={notifIntervalHours}
                        onChange={(e) => setNotifIntervalHours(Number(e.target.value))}
                        className="px-3 py-2 rounded-lg border border-line bg-surface text-content text-xs focus:outline-none focus:border-primary-500"
                      >
                        {[1, 2, 4, 6, 12, 24].map((h) => (
                          <option key={h} value={h}>
                            {h} {h === 1 ? "hora" : "horas"}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-100 text-red-700 text-sm font-medium">
              {error}
            </div>
          )}

          <div className="flex gap-3 border-t border-line pt-4 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              icon={isSubmitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
              className="flex-1"
              disabled={isSubmitting || !companyId}
              title={!companyId ? "Primero selecciona una compañía" : ""}
              onClick={(e) => {
                if (!companyId) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              {isSubmitting ? "Creando..." : "Crear envío"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
